import polylabel from 'polylabel';
import type { MappedShapeData } from './ShapeMapper.svelte';

const LABEL_LAYER_ID = 'store-labels-layer';

export function clearAllLabels(svgElement: SVGSVGElement) {
    const existingLayer = svgElement.querySelector(`#${LABEL_LAYER_ID}`);
    if (existingLayer) {
        existingLayer.remove();
    }
}

export function createLabelsForFloor(
    svgElement: SVGSVGElement,
    shapes: MappedShapeData[],
    showLabels: boolean,
    showStorePins: boolean,
    labelStyling: any
) {
    if (!svgElement || !showLabels) {
        clearAllLabels(svgElement);
        return;
    }

    clearAllLabels(svgElement);

    let labelLayer = svgElement.querySelector(`#${LABEL_LAYER_ID}`);
    if (!labelLayer) {
        labelLayer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        labelLayer.setAttribute('id', LABEL_LAYER_ID);
        svgElement.appendChild(labelLayer);
    }

    shapes.forEach(shape => {
        if (shape.labelPosition) {
            // Use pin coordinates for label positioning if available, otherwise use shape center
            const [x, y] = [shape.x, shape.y];
            
            // When pins are visible, move the label down a bit to make space.
            const yOffset = showStorePins ? (labelStyling.yOffset !== undefined ? labelStyling.yOffset : 12) : 0;

            const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
            
            const labelWidth = 100;
            const labelHeight = 40;

            foreignObject.setAttribute('x', String(x - labelWidth / 2));
            foreignObject.setAttribute('y', String(y - labelHeight / 2 + yOffset));
            foreignObject.setAttribute('width', String(labelWidth));
            foreignObject.setAttribute('height', String(labelHeight));
            foreignObject.style.overflow = 'visible';
            foreignObject.style.pointerEvents = 'none';

            const labelDiv = document.createElement('div');
            labelDiv.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
            labelDiv.className = 'store-label';
            labelDiv.innerText = shape.name;
            
            // Get font size based on shape category
            let fontSize = '10px'; // fallback
            switch (shape.areaCategory) {
                case 'Big':
                    fontSize = labelStyling.bigShapeFontSize || '14px';
                    break;
                case 'Medium':
                    fontSize = labelStyling.mediumShapeFontSize || '10px';
                    break;
                case 'Small':
                    fontSize = labelStyling.smallShapeFontSize || '8px';
                    break;
                default:
                    fontSize = labelStyling.mediumShapeFontSize || '10px';
                    break;
            }

            // Styling to make labels readable, with fallbacks
            labelDiv.style.width = '100%';
            labelDiv.style.height = '100%';
            labelDiv.style.display = 'flex';
            labelDiv.style.alignItems = 'center';
            labelDiv.style.justifyContent = 'center';
            labelDiv.style.textAlign = 'center';
            labelDiv.style.fontSize = fontSize;
            labelDiv.style.fontWeight = labelStyling.fontWeight || 'bold';
            labelDiv.style.color = labelStyling.textColor || '#000000';
            labelDiv.style.textShadow = labelStyling.textShadow || '0 0 2px rgba(255,255,255,0.7)';
            labelDiv.style.pointerEvents = 'none';

            foreignObject.appendChild(labelDiv);
            labelLayer.appendChild(foreignObject);
        }
    });
}

const HOVER_LABEL_LAYER_ID = 'store-hover-label-layer';

export function clearHoverLabel(svgElement: SVGSVGElement) {
    const existingLayer = svgElement.querySelector(`#${HOVER_LABEL_LAYER_ID}`);
    if (existingLayer) {
        existingLayer.remove();
    }
}

export function createHoverLabel(
    svgElement: SVGSVGElement,
    shape: MappedShapeData,
    showStorePins: boolean,
    labelStyling: any
) {
    if (!svgElement || !shape || !shape.labelPosition) return;

    clearHoverLabel(svgElement);

    let labelLayer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    labelLayer.setAttribute('id', HOVER_LABEL_LAYER_ID);
    svgElement.appendChild(labelLayer);

    // Use pin coordinates for label positioning if available, otherwise use shape center
    const [x, y] = [shape.x, shape.y];
    const yOffset = showStorePins ? (labelStyling.yOffset !== undefined ? labelStyling.yOffset : 12) : 0;

    const foreignObject = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'foreignObject'
    );

    // Make the foreignObject smaller and let it size to content
    const labelWidth = 200;  // Wider to accommodate longer text
    const labelHeight = 30;  // Smaller height

    foreignObject.setAttribute('x', String(x - labelWidth / 2));
    foreignObject.setAttribute('y', String(y - labelHeight / 2 + yOffset));
    foreignObject.setAttribute('width', String(labelWidth));
    foreignObject.setAttribute('height', String(labelHeight));
    foreignObject.style.overflow = 'visible';
    foreignObject.style.pointerEvents = 'none';

    // Create a centering container
    const containerDiv = document.createElement('div');
    containerDiv.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
    containerDiv.style.width = '100%';
    containerDiv.style.height = '100%';
    containerDiv.style.display = 'flex';
    containerDiv.style.alignItems = 'center';
    containerDiv.style.justifyContent = 'center';

    const labelDiv = document.createElement('div');
    labelDiv.className = 'store-label store-hover-label';
    labelDiv.innerText = shape.name;

    // Get font size based on shape category
    let fontSize = '10px'; // fallback
    switch (shape.areaCategory) {
        case 'Big':
            fontSize = labelStyling.bigShapeFontSize || '12px';
            break;
        case 'Medium':
            fontSize = labelStyling.mediumShapeFontSize || '10px';
            break;
        case 'Small':
            fontSize = labelStyling.smallShapeFontSize || '8px';
            break;
        default:
            fontSize = labelStyling.mediumShapeFontSize || '10px';
            break;
    }

    // Apply styles - make the label size to its content, not the container
    labelDiv.style.display = 'inline-block';
    labelDiv.style.textAlign = 'center';
    labelDiv.style.fontSize = fontSize;
    labelDiv.style.fontWeight = labelStyling.fontWeight || 'bold';
    labelDiv.style.color = labelStyling.textColor || '#000000';
    labelDiv.style.textShadow =
        labelStyling.textShadow || '0 0 2px rgba(255,255,255,0.7)';
    labelDiv.style.pointerEvents = 'none';
    labelDiv.style.whiteSpace = 'nowrap';
    
    // Add a subtle background to distinguish it as a hover label
    labelDiv.style.backgroundColor = labelStyling.hoverBackgroundColor || 'rgba(255, 255, 255, 0.8)';
    labelDiv.style.borderRadius = labelStyling.hoverBorderRadius || '4px';

    const verticalPadding = labelStyling.hoverVerticalPadding || '2px';
    const horizontalPadding = labelStyling.hoverHorizontalPadding || '4px';
    labelDiv.style.padding = `${verticalPadding} ${horizontalPadding}`;

    containerDiv.appendChild(labelDiv);
    foreignObject.appendChild(containerDiv);
    labelLayer.appendChild(foreignObject);
}

// A point is an array of two numbers, [x, y]
type Point = [number, number];
// A polygon is an array of points
type Polygon = Point[];

/**
 * Calculates the center of the bounding box of a polygon.
 * @param polygon The polygon.
 * @returns The center point of the bounding box.
 */
function getBoundingBoxCenter(polygon: Polygon): Point {
    let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

    for (const [x, y] of polygon) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
    }

    return [(minX + maxX) / 2, (minY + maxY) / 2];
}

/**
 * Get the optimal position for a label within a polygon.
 * It uses polylabel to find the position, but falls back to the center of the
 * bounding box if polylabel returns NaN values.
 * @param polygon The polygon to find the label position for.
 * @returns The [x, y] coordinates for the label.
 */
export function getLabelPosition(polygon: Polygon): Point {
    // Polylabel requires a specific format for polygons, an array of arrays of points.
    // Our polygon is just an array of points, so we wrap it.
    const polylabelPosition = polylabel([polygon]);

    // Check if polylabel returned valid coordinates
    if (!isNaN(polylabelPosition[0]) && !isNaN(polylabelPosition[1])) {
        return [polylabelPosition[0], polylabelPosition[1]];
    }

    // Fallback to bounding box center if polylabel fails
    return getBoundingBoxCenter(polygon);
} 